import { useQuery } from '@tanstack/react-query';
import { format } from 'date-fns';
import { useContext } from 'react';

import fortress from '../api/fortress';
import { AuthContext } from '../contexts/AuthContext';
import { BrandContext } from '../contexts/BrandContext';
import { useGlobalStore } from '../store/store';

export const useBrandPerformanceMetrics = () => {
  const { token } = useContext(AuthContext);
  const { brand } = useContext(BrandContext);
  const { dates, currency, marketplaces } = useGlobalStore();

  const params = {
    brandid: brand.brandId,
    tf_s: format(dates.tf_s, 'yyyy-MM-dd'),
    tf_e: format(dates.tf_e, 'yyyy-MM-dd'),
    wow: dates.timeframe,
    currency: currency,
    marketplace: marketplaces,
  };

  return useQuery({
    queryKey: ['BrandPerformanceMetrics', params, token],
    queryFn: () =>
      fortress.get<IBrandPerformanceMetricsResponse>('brand/performance/performanceMetrics', {
        headers: { Authorization: `Bearer ${token}` },
        params,
      }),
    // enabled: false,
    throwOnError: () => {
      return false;
    },
  });
};

interface IBrandPerformanceMetricsResponse {
  dataTable: IBrandPerformanceMetricsDataTable[];
  lineChart: {
    data: Record<
      string,
      {
        label: string;
        data: [number, number];
      }
    >;
    labels: string[];
  };
}

export interface IBrandPerformanceMetricsDataTable {
  blended_roas: number;
  buybox_pageviews: number;
  buybox_percentage: number;
  date: string;
  dsp_brandSearch14d: number;
  dsp_clicks: number;
  dsp_dpv14d: number;
  dsp_impressions: number;
  dsp_orders: number;
  dsp_purchases14d: number;
  dsp_totalCost: number;
  dsp_totalNewToBrandProductSales14d: number;
  dsp_totalSales14d: number;
  dsp_units: number;
  new_to_brand_customers: number;
  new_to_brand_sales: number;
  new_to_brand_units: number;
  organic_cnv: number;
  organic_orders: number;
  organic_sales: number;
  organic_sessions: number;
  organic_units: number;
  paid_acos: number;
  paid_clicks: number;
  paid_cnv_orders: number;
  paid_cnv_units: number;
  paid_cpa: number;
  paid_cpc: number;
  paid_cpm: number;
  paid_ctr: number;
  paid_impressions: number;
  paid_orders: number;
  paid_roas: number;
  paid_sales: number;
  paid_spend: number;
  paid_units: number;
  py_blended_roas: number;
  py_buybox_pageviews: number;
  py_buybox_percentage: number;
  py_change_blended_roas: number;
  py_change_buybox_pageviews: number;
  py_change_buybox_percentage: number;
  py_change_dsp_brandSearch14d: number;
  py_change_dsp_clicks: number;
  py_change_dsp_dpv14d: number;
  py_change_dsp_impressions: number;
  py_change_dsp_orders: number;
  py_change_dsp_purchases14d: number;
  py_change_dsp_totalCost: number;
  py_change_dsp_totalNewToBrandProductSales14d: number;
  py_change_dsp_totalSales14d: number;
  py_change_dsp_units: number;
  py_change_new_to_brand_customers: number;
  py_change_new_to_brand_sales: number;
  py_change_new_to_brand_units: number;
  py_change_organic_cnv: number;
  py_change_organic_orders: number;
  py_change_organic_sales: number;
  py_change_organic_sessions: number;
  py_change_organic_units: number;
  py_change_paid_acos: number;
  py_change_paid_clicks: number;
  py_change_paid_cnv_orders: number;
  py_change_paid_cnv_units: number;
  py_change_paid_cpa: number;
  py_change_paid_cpc: number;
  py_change_paid_cpm: number;
  py_change_paid_ctr: number;
  py_change_paid_impressions: number;
  py_change_paid_orders: number;
  py_change_paid_roas: number;
  py_change_paid_sales: number;
  py_change_paid_spend: number;
  py_change_paid_units: number;
  py_change_pct_blended_roas: number;
  py_change_pct_buybox_pageviews: number;
  py_change_pct_buybox_percentage: number;
  py_change_pct_dsp_brandSearch14d: '';
  py_change_pct_dsp_clicks: '';
  py_change_pct_dsp_dpv14d: '';
  py_change_pct_dsp_impressions: '';
  py_change_pct_dsp_orders: '';
  py_change_pct_dsp_purchases14d: '';
  py_change_pct_dsp_totalCost: '';
  py_change_pct_dsp_totalNewToBrandProductSales14d: '';
  py_change_pct_dsp_totalSales14d: '';
  py_change_pct_dsp_units: '';
  py_change_pct_new_to_brand_customers: number;
  py_change_pct_new_to_brand_sales: number;
  py_change_pct_new_to_brand_units: number;
  py_change_pct_organic_cnv: number;
  py_change_pct_organic_orders: number;
  py_change_pct_organic_sales: number;
  py_change_pct_organic_sessions: number;
  py_change_pct_organic_units: number;
  py_change_pct_paid_acos: number;
  py_change_pct_paid_clicks: number;
  py_change_pct_paid_cnv_orders: number;
  py_change_pct_paid_cnv_units: number;
  py_change_pct_paid_cpa: number;
  py_change_pct_paid_cpc: number;
  py_change_pct_paid_cpm: number;
  py_change_pct_paid_ctr: number;
  py_change_pct_paid_impressions: number;
  py_change_pct_paid_orders: number;
  py_change_pct_paid_roas: number;
  py_change_pct_paid_sales: number;
  py_change_pct_paid_spend: number;
  py_change_pct_paid_units: number;
  py_change_pct_repeat_customers: number;
  py_change_pct_repeat_sales: number;
  py_change_pct_repeat_units: number;
  py_change_pct_sb_acos: number;
  py_change_pct_sb_clicks: number;
  py_change_pct_sb_cnv_orders: number;
  py_change_pct_sb_cpa: number;
  py_change_pct_sb_cpc: number;
  py_change_pct_sb_cpm: number;
  py_change_pct_sb_ctr: number;
  py_change_pct_sb_impressions: number;
  py_change_pct_sb_orders: number;
  py_change_pct_sb_roas: number;
  py_change_pct_sb_sales: number;
  py_change_pct_sb_spend: number;
  py_change_pct_sd_acos: number;
  py_change_pct_sd_clicks: number;
  py_change_pct_sd_cnv_orders: number;
  py_change_pct_sd_cnv_units: number;
  py_change_pct_sd_cpa: number;
  py_change_pct_sd_cpc: number;
  py_change_pct_sd_cpm: number;
  py_change_pct_sd_ctr: number;
  py_change_pct_sd_impressions: number;
  py_change_pct_sd_orders: number;
  py_change_pct_sd_roas: number;
  py_change_pct_sd_sales: number;
  py_change_pct_sd_spend: number;
  py_change_pct_sd_units: number;
  py_change_pct_sns_active_subscriptions: number;
  py_change_pct_sns_sales: number;
  py_change_pct_sns_units: number;
  py_change_pct_sp_acos: number;
  py_change_pct_sp_clicks: number;
  py_change_pct_sp_cnv_orders: number;
  py_change_pct_sp_cnv_units: number;
  py_change_pct_sp_cpa: number;
  py_change_pct_sp_cpc: number;
  py_change_pct_sp_cpm: number;
  py_change_pct_sp_ctr: number;
  py_change_pct_sp_defensive_acos: number;
  py_change_pct_sp_defensive_clicks: number;
  py_change_pct_sp_defensive_cnv_orders: number;
  py_change_pct_sp_defensive_cnv_units: number;
  py_change_pct_sp_defensive_cpa: number;
  py_change_pct_sp_defensive_cpc: number;
  py_change_pct_sp_defensive_cpm: number;
  py_change_pct_sp_defensive_ctr: number;
  py_change_pct_sp_defensive_impressions: number;
  py_change_pct_sp_defensive_orders: number;
  py_change_pct_sp_defensive_roas: number;
  py_change_pct_sp_defensive_sales: number;
  py_change_pct_sp_defensive_spend: number;
  py_change_pct_sp_defensive_units: number;
  py_change_pct_sp_impressions: number;
  py_change_pct_sp_orders: number;
  py_change_pct_sp_performance_acos: number;
  py_change_pct_sp_performance_clicks: number;
  py_change_pct_sp_performance_cnv_orders: number;
  py_change_pct_sp_performance_cnv_units: number;
  py_change_pct_sp_performance_cpa: number;
  py_change_pct_sp_performance_cpc: number;
  py_change_pct_sp_performance_cpm: number;
  py_change_pct_sp_performance_ctr: number;
  py_change_pct_sp_performance_impressions: number;
  py_change_pct_sp_performance_orders: number;
  py_change_pct_sp_performance_roas: number;
  py_change_pct_sp_performance_sales: number;
  py_change_pct_sp_performance_spend: number;
  py_change_pct_sp_performance_units: number;
  py_change_pct_sp_research_acos: number;
  py_change_pct_sp_research_clicks: number;
  py_change_pct_sp_research_cnv_orders: number;
  py_change_pct_sp_research_cnv_units: number;
  py_change_pct_sp_research_cpa: number;
  py_change_pct_sp_research_cpc: number;
  py_change_pct_sp_research_cpm: number;
  py_change_pct_sp_research_ctr: number;
  py_change_pct_sp_research_impressions: number;
  py_change_pct_sp_research_orders: number;
  py_change_pct_sp_research_roas: number;
  py_change_pct_sp_research_sales: number;
  py_change_pct_sp_research_spend: number;
  py_change_pct_sp_research_units: number;
  py_change_pct_sp_roas: number;
  py_change_pct_sp_sales: number;
  py_change_pct_sp_sb_sales: number;
  py_change_pct_sp_sb_spend: number;
  py_change_pct_sp_spend: number;
  py_change_pct_sp_units: number;
  py_change_pct_spsb_acos: number;
  py_change_pct_spsb_clicks: number;
  py_change_pct_spsb_cnv_orders: number;
  py_change_pct_spsb_cpa: number;
  py_change_pct_spsb_cpc: number;
  py_change_pct_spsb_cpm: number;
  py_change_pct_spsb_ctr: number;
  py_change_pct_spsb_impressions: number;
  py_change_pct_spsb_orders: number;
  py_change_pct_spsb_roas: number;
  py_change_pct_total_acos: number;
  py_change_pct_total_cnv_orders: number;
  py_change_pct_total_cnv_units: '';
  py_change_pct_total_customers: number;
  py_change_pct_total_orders: number;
  py_change_pct_total_page_views: number;
  py_change_pct_total_roas: number;
  py_change_pct_total_sales: number;
  py_change_pct_total_sessions: number;
  py_change_pct_total_units: '';
  py_change_repeat_customers: number;
  py_change_repeat_sales: number;
  py_change_repeat_units: number;
  py_change_sb_acos: number;
  py_change_sb_clicks: number;
  py_change_sb_cnv_orders: number;
  py_change_sb_cpa: number;
  py_change_sb_cpc: number;
  py_change_sb_cpm: number;
  py_change_sb_ctr: number;
  py_change_sb_impressions: number;
  py_change_sb_orders: number;
  py_change_sb_roas: number;
  py_change_sb_sales: number;
  py_change_sb_spend: number;
  py_change_sd_acos: number;
  py_change_sd_clicks: number;
  py_change_sd_cnv_orders: number;
  py_change_sd_cnv_units: number;
  py_change_sd_cpa: number;
  py_change_sd_cpc: number;
  py_change_sd_cpm: number;
  py_change_sd_ctr: number;
  py_change_sd_impressions: number;
  py_change_sd_orders: number;
  py_change_sd_roas: number;
  py_change_sd_sales: number;
  py_change_sd_spend: number;
  py_change_sd_units: number;
  py_change_sns_active_subscriptions: number;
  py_change_sns_sales: number;
  py_change_sns_units: number;
  py_change_sp_acos: number;
  py_change_sp_clicks: number;
  py_change_sp_cnv_orders: number;
  py_change_sp_cnv_units: number;
  py_change_sp_cpa: number;
  py_change_sp_cpc: number;
  py_change_sp_cpm: number;
  py_change_sp_ctr: number;
  py_change_sp_defensive_acos: number;
  py_change_sp_defensive_clicks: number;
  py_change_sp_defensive_cnv_orders: number;
  py_change_sp_defensive_cnv_units: number;
  py_change_sp_defensive_cpa: number;
  py_change_sp_defensive_cpc: number;
  py_change_sp_defensive_cpm: number;
  py_change_sp_defensive_ctr: number;
  py_change_sp_defensive_impressions: number;
  py_change_sp_defensive_orders: number;
  py_change_sp_defensive_roas: number;
  py_change_sp_defensive_sales: number;
  py_change_sp_defensive_spend: number;
  py_change_sp_defensive_units: number;
  py_change_sp_impressions: number;
  py_change_sp_orders: number;
  py_change_sp_performance_acos: number;
  py_change_sp_performance_clicks: number;
  py_change_sp_performance_cnv_orders: number;
  py_change_sp_performance_cnv_units: number;
  py_change_sp_performance_cpa: number;
  py_change_sp_performance_cpc: number;
  py_change_sp_performance_cpm: number;
  py_change_sp_performance_ctr: number;
  py_change_sp_performance_impressions: number;
  py_change_sp_performance_orders: number;
  py_change_sp_performance_roas: number;
  py_change_sp_performance_sales: number;
  py_change_sp_performance_spend: number;
  py_change_sp_performance_units: number;
  py_change_sp_research_acos: number;
  py_change_sp_research_clicks: number;
  py_change_sp_research_cnv_orders: number;
  py_change_sp_research_cnv_units: number;
  py_change_sp_research_cpa: number;
  py_change_sp_research_cpc: number;
  py_change_sp_research_cpm: number;
  py_change_sp_research_ctr: number;
  py_change_sp_research_impressions: number;
  py_change_sp_research_orders: number;
  py_change_sp_research_roas: number;
  py_change_sp_research_sales: number;
  py_change_sp_research_spend: number;
  py_change_sp_research_units: number;
  py_change_sp_roas: number;
  py_change_sp_sales: number;
  py_change_sp_sb_sales: number;
  py_change_sp_sb_spend: number;
  py_change_sp_spend: number;
  py_change_sp_units: number;
  py_change_spsb_acos: number;
  py_change_spsb_clicks: number;
  py_change_spsb_cnv_orders: number;
  py_change_spsb_cpa: number;
  py_change_spsb_cpc: number;
  py_change_spsb_cpm: number;
  py_change_spsb_ctr: number;
  py_change_spsb_impressions: number;
  py_change_spsb_orders: number;
  py_change_spsb_roas: number;
  py_change_total_acos: number;
  py_change_total_cnv_orders: number;
  py_change_total_cnv_units: number;
  py_change_total_customers: number;
  py_change_total_orders: number;
  py_change_total_page_views: number;
  py_change_total_roas: number;
  py_change_total_sales: number;
  py_change_total_sessions: number;
  py_change_total_units: number;
  py_date: string;
  py_dsp_brandSearch14d: number;
  py_dsp_clicks: number;
  py_dsp_dpv14d: number;
  py_dsp_impressions: number;
  py_dsp_orders: number;
  py_dsp_purchases14d: number;
  py_dsp_totalCost: number;
  py_dsp_totalNewToBrandProductSales14d: number;
  py_dsp_totalSales14d: number;
  py_dsp_units: number;
  py_new_to_brand_customers: number;
  py_new_to_brand_sales: number;
  py_new_to_brand_units: number;
  py_organic_cnv: number;
  py_organic_orders: number;
  py_organic_sales: number;
  py_organic_sessions: number;
  py_organic_units: number;
  py_paid_acos: number;
  py_paid_clicks: number;
  py_paid_cnv_orders: number;
  py_paid_cnv_units: number;
  py_paid_cpa: number;
  py_paid_cpc: number;
  py_paid_cpm: number;
  py_paid_ctr: number;
  py_paid_impressions: number;
  py_paid_orders: number;
  py_paid_roas: number;
  py_paid_sales: number;
  py_paid_spend: number;
  py_paid_units: number;
  py_repeat_customers: number;
  py_repeat_sales: number;
  py_repeat_units: number;
  py_sb_acos: number;
  py_sb_clicks: number;
  py_sb_cnv_orders: number;
  py_sb_cpa: number;
  py_sb_cpc: number;
  py_sb_cpm: number;
  py_sb_ctr: number;
  py_sb_impressions: number;
  py_sb_orders: number;
  py_sb_roas: number;
  py_sb_sales: number;
  py_sb_spend: number;
  py_sd_acos: number;
  py_sd_clicks: number;
  py_sd_cnv_orders: number;
  py_sd_cnv_units: number;
  py_sd_cpa: number;
  py_sd_cpc: number;
  py_sd_cpm: number;
  py_sd_ctr: number;
  py_sd_impressions: number;
  py_sd_orders: number;
  py_sd_roas: number;
  py_sd_sales: number;
  py_sd_spend: number;
  py_sd_units: number;
  py_sns_active_subscriptions: number;
  py_sns_sales: number;
  py_sns_units: number;
  py_sp_acos: number;
  py_sp_clicks: number;
  py_sp_cnv_orders: number;
  py_sp_cnv_units: number;
  py_sp_cpa: number;
  py_sp_cpc: number;
  py_sp_cpm: number;
  py_sp_ctr: number;
  py_sp_defensive_acos: number;
  py_sp_defensive_clicks: number;
  py_sp_defensive_cnv_orders: number;
  py_sp_defensive_cnv_units: number;
  py_sp_defensive_cpa: number;
  py_sp_defensive_cpc: number;
  py_sp_defensive_cpm: number;
  py_sp_defensive_ctr: number;
  py_sp_defensive_impressions: number;
  py_sp_defensive_orders: number;
  py_sp_defensive_roas: number;
  py_sp_defensive_sales: number;
  py_sp_defensive_spend: number;
  py_sp_defensive_units: number;
  py_sp_impressions: number;
  py_sp_orders: number;
  py_sp_performance_acos: number;
  py_sp_performance_clicks: number;
  py_sp_performance_cnv_orders: number;
  py_sp_performance_cnv_units: number;
  py_sp_performance_cpa: number;
  py_sp_performance_cpc: number;
  py_sp_performance_cpm: number;
  py_sp_performance_ctr: number;
  py_sp_performance_impressions: number;
  py_sp_performance_orders: number;
  py_sp_performance_roas: number;
  py_sp_performance_sales: number;
  py_sp_performance_spend: number;
  py_sp_performance_units: number;
  py_sp_research_acos: number;
  py_sp_research_clicks: number;
  py_sp_research_cnv_orders: number;
  py_sp_research_cnv_units: number;
  py_sp_research_cpa: number;
  py_sp_research_cpc: number;
  py_sp_research_cpm: number;
  py_sp_research_ctr: number;
  py_sp_research_impressions: number;
  py_sp_research_orders: number;
  py_sp_research_roas: number;
  py_sp_research_sales: number;
  py_sp_research_spend: number;
  py_sp_research_units: number;
  py_sp_roas: number;
  py_sp_sales: number;
  py_sp_sb_sales: number;
  py_sp_sb_spend: number;
  py_sp_spend: number;
  py_sp_units: number;
  py_spsb_acos: number;
  py_spsb_clicks: number;
  py_spsb_cnv_orders: number;
  py_spsb_cpa: number;
  py_spsb_cpc: number;
  py_spsb_cpm: number;
  py_spsb_ctr: number;
  py_spsb_impressions: number;
  py_spsb_orders: number;
  py_spsb_roas: number;
  py_total_acos: number;
  py_total_cnv_orders: number;
  py_total_cnv_units: number;
  py_total_customers: number;
  py_total_orders: number;
  py_total_page_views: number;
  py_total_roas: number;
  py_total_sales: number;
  py_total_sessions: number;
  py_total_units: number;
  repeat_customers: number;
  repeat_sales: number;
  repeat_units: number;
  sb_acos: number;
  sb_clicks: number;
  sb_cnv_orders: number;
  sb_cpa: number;
  sb_cpc: number;
  sb_cpm: number;
  sb_ctr: number;
  sb_impressions: number;
  sb_orders: number;
  sb_roas: number;
  sb_sales: number;
  sb_spend: number;
  sd_acos: number;
  sd_clicks: number;
  sd_cnv_orders: number;
  sd_cnv_units: number;
  sd_cpa: number;
  sd_cpc: number;
  sd_cpm: number;
  sd_ctr: number;
  sd_impressions: number;
  sd_orders: number;
  sd_roas: number;
  sd_sales: number;
  sd_spend: number;
  sd_units: number;
  sns_active_subscriptions: number;
  sns_sales: number;
  sns_units: number;
  sp_acos: number;
  sp_clicks: number;
  sp_cnv_orders: number;
  sp_cnv_units: number;
  sp_cpa: number;
  sp_cpc: number;
  sp_cpm: number;
  sp_ctr: number;
  sp_defensive_acos: number;
  sp_defensive_clicks: number;
  sp_defensive_cnv_orders: number;
  sp_defensive_cnv_units: number;
  sp_defensive_cpa: number;
  sp_defensive_cpc: number;
  sp_defensive_cpm: number;
  sp_defensive_ctr: number;
  sp_defensive_impressions: number;
  sp_defensive_orders: number;
  sp_defensive_roas: number;
  sp_defensive_sales: number;
  sp_defensive_spend: number;
  sp_defensive_units: number;
  sp_impressions: number;
  sp_orders: number;
  sp_performance_acos: number;
  sp_performance_clicks: number;
  sp_performance_cnv_orders: number;
  sp_performance_cnv_units: number;
  sp_performance_cpa: number;
  sp_performance_cpc: number;
  sp_performance_cpm: number;
  sp_performance_ctr: number;
  sp_performance_impressions: number;
  sp_performance_orders: number;
  sp_performance_roas: number;
  sp_performance_sales: number;
  sp_performance_spend: number;
  sp_performance_units: number;
  sp_research_acos: number;
  sp_research_clicks: number;
  sp_research_cnv_orders: number;
  sp_research_cnv_units: number;
  sp_research_cpa: number;
  sp_research_cpc: number;
  sp_research_cpm: number;
  sp_research_ctr: number;
  sp_research_impressions: number;
  sp_research_orders: number;
  sp_research_roas: number;
  sp_research_sales: number;
  sp_research_spend: number;
  sp_research_units: number;
  sp_roas: number;
  sp_sales: number;
  sp_sb_sales: number;
  sp_sb_spend: number;
  sp_spend: number;
  sp_units: number;
  spsb_acos: number;
  spsb_clicks: number;
  spsb_cnv_orders: number;
  spsb_cpa: number;
  spsb_cpc: number;
  spsb_cpm: number;
  spsb_ctr: number;
  spsb_impressions: number;
  spsb_orders: number;
  spsb_roas: number;
  total_acos: number;
  total_cnv_orders: number;
  total_cnv_units: number;
  total_customers: number;
  total_orders: number;
  total_page_views: number;
  total_roas: number;
  total_sales: number;
  total_sessions: number;
  total_units: number;
}
